import { useCallback, useEffect, useRef, useState } from 'react';
import { AdCreative } from '../../api/ads/ads.api';
import { IPostModel } from '../../models';
import { fetchPreRollAd, sameUser } from '../../components/ads/use-pre-roll';
import { useViewerProStatus } from '../../components/ads/use-viewer-pro';
import { claimPreRollSlot, markPreRollMissed, notePlaybackStart } from '../../components/ads/pre-roll-frequency';
import { REEL_AD_EVERY } from './reels-display-list';

/** Trạng thái quảng cáo chèn trước của một reel rơi vào ô quảng cáo (reel thứ 6, 12, ...). */
type SlotState = { kind: 'pending' } | { kind: 'ad'; ad: AdCreative } | { kind: 'done'; shown: boolean };

const isVideoPost = (p?: IPostModel | null) => !!p && p.postType === 'video' && (p.postVideoResponses?.length || 0) > 0;
export const isPreRollSlot = (postIndex: number) => (postIndex + 1) % REEL_AD_EVERY === 0;

export interface ReelPreRoll {
  /** Reel này phải dừng chờ (đang hỏi quảng cáo hoặc đang chạy quảng cáo). */
  holds: (post: IPostModel, postIndex: number) => boolean;
  /** Quảng cáo đang chạy trước reel này (nếu có). */
  adFor: (postId: string) => AdCreative | null;
  /** Quảng cáo trước reel này kết thúc. */
  finish: (postId: string, started: boolean) => void;
  /** Quảng cáo trước reel này vừa thật sự bắt đầu chạy: bỏ ô quảng cáo sau reel đó ngay. */
  started: (postId: string) => void;
  /** Reel đã có quảng cáo chèn trước thật sự chạy: ô quảng cáo sau reel đó không chèn nữa. */
  preRolled: Set<string>;
}

/**
 * Quảng cáo chèn trước trong Reel (spec 05/11 đợt 3): ở reel rơi vào ô quảng cáo (mỗi 6 reel), lúc reel đó bắt đầu chạy thì
 * hỏi một quảng cáo chèn trước (tối đa 1,5 giây, reel dừng chờ). Có quảng cáo thì chạy trước reel và ô quảng cáo sau reel đó
 * bị bỏ; không có (hoặc chưa đủ 2 phút từ quảng cáo trước, người xem có Cab+ Pro, reel của chính người xem) thì ô quảng cáo giữ
 * nguyên như cũ. Reel video khác chỉ được đếm vào tần suất.
 */
export const useReelPreRoll = (activePost: IPostModel | null, activePostIndex: number, viewerId?: string): ReelPreRoll => {
  const { isPro } = useViewerProStatus();
  const [slots, setSlots] = useState<Record<string, SlotState>>({});
  const slotsRef = useRef(slots);
  slotsRef.current = slots;
  const counted = useRef<Set<string>>(new Set());
  const [preRolled, setPreRolled] = useState<Set<string>>(() => new Set());
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const setSlot = (id: string, s: SlotState) => setSlots((prev) => ({ ...prev, [id]: s }));

  const started = useCallback((postId: string) => {
    setPreRolled((prev) => (prev.has(postId) ? prev : new Set(prev).add(postId)));
  }, []);
  const finish = useCallback((postId: string, didStart: boolean) => {
    setSlot(postId, { kind: 'done', shown: didStart });
    if (didStart) started(postId);
  }, [started]);

  const activeId = activePost?.id;
  useEffect(() => {
    if (!activePost || !isVideoPost(activePost)) return;
    const id = activePost.id;
    if (slotsRef.current[id] || counted.current.has(id)) return;
    if (!isPreRollSlot(activePostIndex)) {
      counted.current.add(id);
      if (!sameUser(viewerId, (activePost as any).userId)) notePlaybackStart();
      return;
    }
    if (sameUser(viewerId, (activePost as any).userId) || isPro || !claimPreRollSlot()) {
      setSlot(id, { kind: 'done', shown: false });
      return;
    }
    setSlot(id, { kind: 'pending' });
    fetchPreRollAd(id).then((ad) => {
      if (!alive.current) return;
      if (slotsRef.current[id]?.kind !== 'pending') return; // người xem đã lướt đi
      if (!ad) {
        markPreRollMissed();
        setSlot(id, { kind: 'done', shown: false });
      } else setSlot(id, { kind: 'ad', ad });
    });
    // activePost đổi theo id; chỉ xét lại khi đổi reel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, activePostIndex, viewerId, isPro]);

  // Lướt khỏi reel đang chờ hoặc đang chạy quảng cáo: bỏ quảng cáo đó (đã chạy thì vẫn tính là đã chèn).
  useEffect(() => {
    Object.entries(slotsRef.current).forEach(([id, s]) => {
      if (id === activeId) return;
      if (s.kind === 'pending') {
        markPreRollMissed();
        setSlot(id, { kind: 'done', shown: false });
      }
      if (s.kind === 'ad') setSlot(id, { kind: 'done', shown: false }); // đã chạy thì preRolled đã có id này từ lúc bắt đầu
    });
  }, [activeId]);

  const holds = useCallback(
    (post: IPostModel, postIndex: number) => {
      if (!isVideoPost(post) || !isPreRollSlot(postIndex)) return false;
      const s = slots[post.id];
      // Chưa xét (lượt vẽ ngay khi reel vừa được chọn) cũng dừng, để reel không kịp chạy một nhịp rồi mới dừng.
      return !s || s.kind === 'pending' || s.kind === 'ad';
    },
    [slots]
  );
  const adFor = useCallback((postId: string) => {
    const s = slots[postId];
    return s && s.kind === 'ad' ? s.ad : null;
  }, [slots]);

  return { holds, adFor, finish, started, preRolled };
};

/** Bỏ ô quảng cáo sau những reel đã có quảng cáo chèn trước chạy. */
export const withoutPreRolled = <A>(ads: Record<string, A>, preRolled: Set<string>): Record<string, A> => {
  if (!preRolled.size) return ads;
  const out: Record<string, A> = {};
  Object.entries(ads).forEach(([id, a]) => {
    if (!preRolled.has(id)) out[id] = a;
  });
  return out;
};
